import { useEffect } from 'react';
import { LoadingSpinerArea } from '../../../../components';
import { Alert, Container } from 'rsuite';
import { ModalSpeakWithSpecialist } from '../../../../components/ModalSpeakWithSpecialist';
import { EmptyStateSecond } from '../../../CalculadoraPrevisaoDemanda/Components';
import { PageBlocker } from '../InfoPanel/Components/PageBlocker';
import ModalConfirm from '../../../../components/ModalConfirm';
import {
  BigNumbersArea,
  Detailing,
  EmptyState,
  FiltersArea,
  HeaderAderenciaPrecos,
  HistoryPriceOutOfRangeChart,
  NotificationPendingUpdate,
  ProductsByRangeChart,
  ResumoChartBox,
} from './components';
import { useAderenciaBigNumbers } from './hooks/useAderenciaBigNumbers';
import { useAderenciaDetailing } from './hooks/useAderenciaDetailing';
import { useAderenciaHistory } from './hooks/useAderenciaHistory';
import { useAderenciaStatus } from './hooks/useAderenciaStatus';
import { useProductsByRange } from './hooks/useProductsByRange';
import { useAderenciaProductFilter } from './hooks/useAderenciaProductFilter';
import { useAderenciaCounter } from './hooks/useAderenciaCounter';
import { trackEvent } from '../../../../utils/MatomoConfig';
import { sendLead } from '../InfoPanel/utils';
import { INFOPANEL_CTA_LINK } from '../../../../utils/constants';
import { aderenciaInitialState, useAderenciaPrecosStore } from './AderenciaPrecos.store';
import { FILE_DOWNLOAD_STATUS, FILTER_NAMES, PAGE_BLOCKER, TRACK_EVENT_FILTER_NAMES, ADERENCIA_BODY_REQUEST_NAMES } from './data';
import { User } from '../../../../@types';
import { FilterNames } from './types';
import styles from './AderenciaPrecos.module.scss';

type AderenciaPrecosProps = {
  usuario: User;
};

export const AderenciaPrecos = ({ usuario }: AderenciaPrecosProps) => {
  const {
    showModalSpeakWithSpecialistAderenciaPrecos,
    aderenciaDetailsBodyRequest,
    aderenciaBodyRequest,
    aderenciaBigNumbersData,
    aderenciaProductByRangeData,
    aderenciaDetailsData,
    showModalDetailsDownload,
    fileDownloadStatusType,
    setAderenciaPrecosState,
    setAderenciaSelectSavedFilter,
    setAderenciaDetailsValuesCleanFilters,
    reset,
  } = useAderenciaPrecosStore();

  const ADERENCIA_INITIAL_STATE = aderenciaInitialState;

  const { isLoadingBigNumbers, aderenciaStatus, handleGetAderenciaBigNumbers } = useAderenciaBigNumbers();
  const { isLoadingDetails, handleDetailsPageChange, handleDetailsLengthChange, handleGetAderenciaDetails, handleDetailsSortChange } = useAderenciaDetailing();

  const { isLoadingProductByRange, handleRequestProductByRange } = useProductsByRange();

  const { isLoadingHistory, handleGetAderenciaHistoryChart } = useAderenciaHistory();

  const { aderenciaStatusData, aderenciaEntryStatus } = useAderenciaStatus();

  const { applyCopyPasteEansToProductFilter } = useAderenciaProductFilter();

  const { handleGetAderenciaDetailsCount } = useAderenciaCounter();

  const HAS_NO_PERMISSION = !usuario?.tipoAdicionalContratos?.PAN?.includes('ADERENCIA_PRECOS');

  const handleShowPageBlockModal = (show: boolean) => {
    setAderenciaPrecosState('showModalSpeakWithSpecialistAderenciaPrecos', show);
  };

  const sendCTA = () => {
    sendLead(usuario, 'upsell-aderencia-precos');
    trackEvent('Upsell/Crossell', 'upsell-block-aderencia-precos');
    handleShowPageBlockModal(false);
    window.open(INFOPANEL_CTA_LINK);
  };

  const handleValidationBeforeDownload = (value: 'CSV' | 'XLSX') => {
    setAderenciaPrecosState('detailsDownloadType', value);
    handleGetAderenciaDetailsCount();
  };

  const handleRequestAderenciaData = async (body: typeof aderenciaBodyRequest) => {
    const detailsBody = { ...aderenciaDetailsBodyRequest, ...body };

    try {
      await handleGetAderenciaBigNumbers(body);
      await handleGetAderenciaHistoryChart(body);
      await handleRequestProductByRange(body);
      await handleGetAderenciaDetails(detailsBody);
    } catch (error) {
      console.error('Erro ao buscar dados de aderência:', error);
      Alert.error('Erro ao buscar dados de aderência. Tente novamente.', 5000);
    }
  };

  const handleFiltersOnClean = (name: FilterNames) => {
    const body = { ...aderenciaBodyRequest, [ADERENCIA_BODY_REQUEST_NAMES[name]]: [] };
    handleRequestAderenciaData(body);
  };

  const handleFiltersOnClose = (name: FilterNames, body: typeof aderenciaBodyRequest) => {
    handleRequestAderenciaData(body);
    setAderenciaDetailsValuesCleanFilters();

    trackEvent('Aderência de preços', TRACK_EVENT_FILTER_NAMES[name]);
  };

  const handleChangePreferences = () => {
    handleRequestAderenciaData(ADERENCIA_INITIAL_STATE.aderenciaBodyRequest);
    trackEvent('Aderência de preços', 'Preferências');
  };

  const handleDetailingFiltersOnClose = () => {
    handleGetAderenciaDetails(aderenciaDetailsBodyRequest);
    trackEvent('Aderência de preços', 'Filtro de detalhamento');
  };

  const handleDetailingFiltersOnClean = (name: FilterNames) => {
    const body = { ...aderenciaDetailsBodyRequest, [FILTER_NAMES[name]]: aderenciaBodyRequest[FILTER_NAMES[name]] || [] };
    handleGetAderenciaDetails(body);
  };

  const handleSelectSavedFilter = (id: number) => {
    setAderenciaSelectSavedFilter(id, handleRequestAderenciaData, applyCopyPasteEansToProductFilter);
  };

  const handleDateChange = (startDate: string, endDate: string) => {
    const body = { ...aderenciaBodyRequest, dataInicial: startDate, dataFinal: endDate };
    setAderenciaPrecosState('aderenciaBodyRequest', body);
    handleRequestAderenciaData(body);
    trackEvent('Aderência de preços', 'Filtro de período');
  };

  const handleDateClean = () => {
    const body = { ...aderenciaBodyRequest, dataInicial: '', dataFinal: '' };
    setAderenciaPrecosState('aderenciaBodyRequest', body);
    handleRequestAderenciaData(body);
  };

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const PENDING_DATA_NOTIFICATION = aderenciaStatusData?.atualizacaoPendente;
  const HAS_NOT_CONFIGURED = aderenciaEntryStatus === 204;
  const HAS_DATA = aderenciaStatus === 200;
  const START_PENDING_DATA = aderenciaEntryStatus === 200 && aderenciaStatusData?.totalDeDados === 0 && PENDING_DATA_NOTIFICATION;
  const MAIN_CONTENT_ACCESS = aderenciaEntryStatus === 200 && aderenciaStatusData?.totalDeDados > 0;
  const STATUS_HAS_REQUESTED = typeof aderenciaEntryStatus === 'number';

  return (
    <Container className={`main-container ${styles['aderencia-precos-container']}`}>
      <div id="container" className={styles['inner-container']}>
        <LoadingSpinerArea area="aderencia-entry-status" size="md" style={{ position: 'absolute' }} />
        <HeaderAderenciaPrecos status={aderenciaStatus} />

        {HAS_NO_PERMISSION && STATUS_HAS_REQUESTED && (
          <PageBlocker
            id="analise-competitividade-blocker-btn"
            className={styles['page-blocker-aderencia']}
            title={PAGE_BLOCKER.title}
            subtitle={PAGE_BLOCKER.subtitle}
            buttonText="quero o teste grátis"
            onClick={() => handleShowPageBlockModal(true)}
          />
        )}

        {START_PENDING_DATA && <EmptyState type="PENDING" />}

        {HAS_NOT_CONFIGURED && <EmptyState type="NO_DATA" />}

        {MAIN_CONTENT_ACCESS && (
          <div className={styles['content-wrapper']}>
            <FiltersArea
              onClose={(name: FilterNames) => handleFiltersOnClose(name, aderenciaBodyRequest)}
              onClean={handleFiltersOnClean}
              onSelectSavedFilter={handleSelectSavedFilter}
              onCleanSavedFilter={() => handleRequestAderenciaData(ADERENCIA_INITIAL_STATE.aderenciaBodyRequest)}
              onChangePreference={handleChangePreferences}
              onDateClean={handleDateClean}
              onDateChange={handleDateChange}
            />

            {HAS_DATA ? (
              <>
                {PENDING_DATA_NOTIFICATION && <NotificationPendingUpdate />}

                <BigNumbersArea isLoading={isLoadingBigNumbers} data={aderenciaBigNumbersData} />

                <div className={styles['chart-content-wrapper']}>
                  <ResumoChartBox isLoading={isLoadingBigNumbers} data={aderenciaBigNumbersData} />
                  <HistoryPriceOutOfRangeChart isLoading={isLoadingHistory} />
                </div>

                <ProductsByRangeChart isLoading={isLoadingProductByRange} data={aderenciaProductByRangeData} />

                <Detailing
                  loading={isLoadingDetails}
                  data={aderenciaDetailsData}
                  onChangePage={handleDetailsPageChange}
                  onChangeLength={handleDetailsLengthChange}
                  onClickDownload={handleValidationBeforeDownload}
                  onClose={handleDetailingFiltersOnClose}
                  onClean={handleDetailingFiltersOnClean}
                  onSortColumn={handleDetailsSortChange}
                />
              </>
            ) : (
              <div style={{ position: 'relative' }}>
                <LoadingSpinerArea area="aderencia-big-numbers" size="md" style={{ position: 'absolute' }} />
                <EmptyStateSecond />
              </div>
            )}
          </div>
        )}
        <ModalSpeakWithSpecialist
          show={showModalSpeakWithSpecialistAderenciaPrecos}
          style={{ width: '550px' }}
          title={PAGE_BLOCKER.modal.title}
          subtitle={PAGE_BLOCKER.modal.subtitle}
          onClick={sendCTA}
          onCancel={() => handleShowPageBlockModal(false)}
        />
        <ModalConfirm
          show={showModalDetailsDownload}
          modalWidth="490px"
          titleFontSize="16px"
          textAlign="center"
          footerAlign="center"
          closeButton={FILE_DOWNLOAD_STATUS[fileDownloadStatusType]?.closeButton}
          cancelButtonWidth={FILE_DOWNLOAD_STATUS[fileDownloadStatusType]?.cancelBtnWidth}
          confirmButtonWidth={FILE_DOWNLOAD_STATUS[fileDownloadStatusType]?.confirmBtnWidth}
          icon={FILE_DOWNLOAD_STATUS[fileDownloadStatusType]?.icon}
          title={FILE_DOWNLOAD_STATUS[fileDownloadStatusType]?.title}
          message={FILE_DOWNLOAD_STATUS[fileDownloadStatusType]?.message}
          confirmButton={FILE_DOWNLOAD_STATUS[fileDownloadStatusType]?.confirmButton}
          cancelButton={FILE_DOWNLOAD_STATUS[fileDownloadStatusType]?.cancelButton}
          onHide={() => setAderenciaPrecosState('showModalDetailsDownload', false)}
          onCancel={() => setAderenciaPrecosState('showModalDetailsDownload', false)}
          onConfirm={() => setAderenciaPrecosState('showModalDetailsDownload', false)}
        />
      </div>
    </Container>
  );
};
